import polygonClipping, {
  type Polygon as PCPolygon,
  type Ring as PCRing,
} from "polygon-clipping";
import type { Vec2 } from "./types";

export const IN_PER_FT = 12;

/** Format inches as feet'inches" or cm depending on unit. */
export function formatLength(inches: number, unit: "imperial" | "metric"): string {
  if (!Number.isFinite(inches)) return "—";
  if (unit === "metric") {
    const cm = inches * 2.54;
    if (cm >= 100) return `${(cm / 100).toFixed(2)} m`;
    return `${Math.round(cm)} cm`;
  }
  const totalInches = Math.round(inches);
  const ft = Math.floor(totalInches / 12);
  const inch = totalInches - ft * 12;
  if (ft === 0) return `${inch}"`;
  if (inch === 0) return `${ft}'`;
  return `${ft}'${inch}"`;
}

export function formatArea(squareInches: number, unit: "imperial" | "metric"): string {
  if (unit === "metric") {
    const m2 = squareInches * 0.00064516;
    return `${m2.toFixed(2)} m²`;
  }
  const sqft = squareInches / 144;
  return `${sqft.toFixed(1)} ft²`;
}

/** Shoelace formula. Returns positive area in same units squared. */
export function polygonArea(points: Vec2[]): number {
  const n = points.length;
  if (n < 3) return 0;
  let sum = 0;
  for (let i = 0; i < n; i++) {
    const a = points[i];
    const b = points[(i + 1) % n];
    sum += a.x * b.y - b.x * a.y;
  }
  return Math.abs(sum) / 2;
}

export function polygonPerimeter(points: Vec2[]): number {
  let total = 0;
  const n = points.length;
  for (let i = 0; i < n; i++) {
    total += distance(points[i], points[(i + 1) % n]);
  }
  return total;
}

export function distance(a: Vec2, b: Vec2): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return Math.sqrt(dx * dx + dy * dy);
}

export function midpoint(a: Vec2, b: Vec2): Vec2 {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/** Returns bounding box of a polygon. */
export function boundingBox(points: Vec2[]) {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
}

/** Ray casting point-in-polygon test. */
export function pointInPolygon(point: Vec2, polygon: Vec2[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x,
      yi = polygon[i].y;
    const xj = polygon[j].x,
      yj = polygon[j].y;
    const intersect =
      yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** Unit vector along a wall edge. */
export function wallDirection(start: Vec2, end: Vec2): Vec2 {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len === 0) return { x: 1, y: 0 };
  return { x: dx / len, y: dy / len };
}

/** Outward-facing wall normal (assumes polygon is CCW in screen coords / CW in math coords). */
export function wallNormal(start: Vec2, end: Vec2): Vec2 {
  const dir = wallDirection(start, end);
  return { x: -dir.y, y: dir.x };
}

/** Wall angle in degrees (0 = pointing right). */
export function wallAngleDeg(start: Vec2, end: Vec2): number {
  return (Math.atan2(end.y - start.y, end.x - start.x) * 180) / Math.PI;
}

/** Generate preset room shapes by overall dimensions (inches). */
export function presetShape(
  kind: "rectangle" | "l-shape" | "u-shape" | "t-shape",
  width: number,
  depth: number
): Vec2[] {
  // Origin at top-left.
  switch (kind) {
    case "rectangle":
      return [
        { x: 0, y: 0 },
        { x: width, y: 0 },
        { x: width, y: depth },
        { x: 0, y: depth },
      ];
    case "l-shape": {
      const w2 = width * 0.6;
      const d2 = depth * 0.55;
      return [
        { x: 0, y: 0 },
        { x: w2, y: 0 },
        { x: w2, y: d2 },
        { x: width, y: d2 },
        { x: width, y: depth },
        { x: 0, y: depth },
      ];
    }
    case "u-shape": {
      const sideW = width * 0.3;
      const cutD = depth * 0.55;
      return [
        { x: 0, y: 0 },
        { x: sideW, y: 0 },
        { x: sideW, y: cutD },
        { x: width - sideW, y: cutD },
        { x: width - sideW, y: 0 },
        { x: width, y: 0 },
        { x: width, y: depth },
        { x: 0, y: depth },
      ];
    }
    case "t-shape": {
      const topW = width;
      const stemW = width * 0.45;
      const topD = depth * 0.4;
      const offset = (width - stemW) / 2;
      return [
        { x: 0, y: 0 },
        { x: topW, y: 0 },
        { x: topW, y: topD },
        { x: offset + stemW, y: topD },
        { x: offset + stemW, y: depth },
        { x: offset, y: depth },
        { x: offset, y: topD },
        { x: 0, y: topD },
      ];
    }
  }
}

/** Re-scale a polygon edge to a target length.
 *
 * If the edge is axis-aligned (horizontal or vertical) — which is true of all our preset shapes — we
 * preserve the polygon's rectilinearity by moving every vertex that *shares* the relevant coordinate
 * with the edge's endpoint. This lets users edit any wall length on L/U/T shapes without breaking
 * the polygon's closing wall.
 *
 * For non-axis-aligned edges, we fall back to a rigid extension that shifts later vertices.
 */
export function resizeEdge(
  points: Vec2[],
  edgeIndex: number,
  newLength: number
): Vec2[] {
  const n = points.length;
  const a = points[edgeIndex];
  const b = points[(edgeIndex + 1) % n];
  const EPS = 1e-3;
  const horizontal = Math.abs(a.y - b.y) < EPS;
  const vertical = Math.abs(a.x - b.x) < EPS;

  if (horizontal) {
    const direction = b.x >= a.x ? 1 : -1;
    const newXb = a.x + direction * newLength;
    const oldXb = b.x;
    return points.map((p) =>
      Math.abs(p.x - oldXb) < EPS ? { x: newXb, y: p.y } : p
    );
  }
  if (vertical) {
    const direction = b.y >= a.y ? 1 : -1;
    const newYb = a.y + direction * newLength;
    const oldYb = b.y;
    return points.map((p) =>
      Math.abs(p.y - oldYb) < EPS ? { x: p.x, y: newYb } : p
    );
  }

  // Fallback: rigid extension along edge direction.
  const dir = wallDirection(a, b);
  const newB = { x: a.x + dir.x * newLength, y: a.y + dir.y * newLength };
  const dx = newB.x - b.x;
  const dy = newB.y - b.y;
  return points.map((p, i) =>
    i > edgeIndex ? { x: p.x + dx, y: p.y + dy } : p
  );
}

/** Resize an edge while iteratively re-enforcing the lengths of other locked
 *  edges. Used by Shape mode's "lock-in measurements" feature so that typing a
 *  new dimension on wall B doesn't silently change wall A's previously locked
 *  length on shapes (L/U/T) where edits share corner coordinates.
 *
 *  Algorithm: apply {@link resizeEdge} to the user-requested edge, then for
 *  each locked edge whose length drifted, re-apply `resizeEdge` to restore it.
 *  Iterates up to {@link RESIZE_WITH_LOCKS_MAX_ITER} times — enough for the
 *  rectilinear cases we ship today; conflicting locks (e.g. both parallel
 *  walls of a rectangle pinned at different lengths) will simply settle on the
 *  best-effort polygon rather than oscillating forever.
 *
 *  The lock on `edgeIndex` itself is intentionally NOT re-enforced — the user
 *  is actively setting its length, so we let their new value win. Callers
 *  should update the stored lock value for `edgeIndex` to `newLength` after
 *  this returns so subsequent edits stay consistent. */
export const RESIZE_WITH_LOCKS_MAX_ITER = 6;
export const RESIZE_WITH_LOCKS_TOL_IN = 0.5;

export function resizeEdgeWithLocks(
  points: Vec2[],
  edgeIndex: number,
  newLength: number,
  lockedLengths: Record<number, number>
): Vec2[] {
  let next = resizeEdge(points, edgeIndex, newLength);
  const n = next.length;
  // Snapshot keys once so we don't allocate per iteration.
  const lockedEntries = Object.entries(lockedLengths)
    .map(([k, v]) => [Number(k), v] as const)
    .filter(([idx]) => idx !== edgeIndex && idx >= 0 && idx < n);
  if (lockedEntries.length === 0) return next;

  for (let iter = 0; iter < RESIZE_WITH_LOCKS_MAX_ITER; iter++) {
    let stable = true;
    for (const [idx, targetLen] of lockedEntries) {
      const a = next[idx];
      const b = next[(idx + 1) % n];
      const curLen = Math.hypot(b.x - a.x, b.y - a.y);
      if (Math.abs(curLen - targetLen) > RESIZE_WITH_LOCKS_TOL_IN) {
        next = resizeEdge(next, idx, targetLen);
        stable = false;
      }
    }
    if (stable) break;
  }
  return next;
}

/** Convert a furniture rotation + footprint into 4 corners (room coords). */
export function furnitureCorners(
  cx: number,
  cy: number,
  w: number,
  d: number,
  rotationDeg: number
): Vec2[] {
  const hw = w / 2,
    hd = d / 2;
  const rad = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const corners: Vec2[] = [
    { x: -hw, y: -hd },
    { x: hw, y: -hd },
    { x: hw, y: hd },
    { x: -hw, y: hd },
  ];
  return corners.map((c) => ({
    x: cx + c.x * cos - c.y * sin,
    y: cy + c.x * sin + c.y * cos,
  }));
}

/** True iff every corner of the (rotated) furniture footprint lies inside `polygon`.
 *  Uses {@link pointInPolygon}, which is exclusive on the boundary — a piece
 *  with a corner exactly on a wall counts as outside. That's the safe default
 *  for the AI feng shui validator: we'd rather reject a borderline piece than
 *  let a wall-piercing one through. */
export function furnitureFitsInRoom(
  cx: number,
  cy: number,
  width: number,
  depth: number,
  rotationDeg: number,
  polygon: Vec2[]
): boolean {
  if (polygon.length < 3) return false;
  const corners = furnitureCorners(cx, cy, width, depth, rotationDeg);
  for (const c of corners) {
    if (!pointInPolygon(c, polygon)) return false;
  }
  return true;
}

/** Clamp a point to remain inside a polygon (rough: keep its center inside). */
export function clampInside(p: Vec2, polygon: Vec2[]): Vec2 {
  if (pointInPolygon(p, polygon)) return p;
  // Find nearest point on polygon edges.
  let best: Vec2 = p;
  let bestDist = Infinity;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    const candidate = nearestPointOnSegment(p, a, b);
    const d = distance(candidate, p);
    if (d < bestDist) {
      bestDist = d;
      best = candidate;
    }
  }
  return best;
}

export function nearestPointOnSegment(p: Vec2, a: Vec2, b: Vec2): Vec2 {
  const ax = a.x,
    ay = a.y;
  const bx = b.x,
    by = b.y;
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return { x: ax, y: ay };
  let t = ((p.x - ax) * dx + (p.y - ay) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return { x: ax + t * dx, y: ay + t * dy };
}

/** Result of {@link nearestWallSegment}: the polygon edge closest to a
 *  point, along with the foot of the perpendicular for distance/snap math. */
export interface NearestWall {
  /** Index `i` such that the wall runs from `polygon[i]` to `polygon[(i+1) % n]`. */
  index: number;
  /** Closest point on the wall to the input. */
  closest: Vec2;
  /** Distance from the input to `closest`. */
  distance: number;
}

/** Find the wall (polygon edge) whose nearest point is closest to `p`. Ties
 *  resolve toward the lower wall index. Returns null for degenerate polygons. */
export function nearestWallSegment(p: Vec2, polygon: Vec2[]): NearestWall | null {
  const n = polygon.length;
  if (n < 2) return null;
  let bestIndex = -1;
  let bestClosest: Vec2 = polygon[0];
  let bestDist = Infinity;
  for (let i = 0; i < n; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % n];
    const candidate = nearestPointOnSegment(p, a, b);
    const d = distance(candidate, p);
    if (d < bestDist) {
      bestDist = d;
      bestIndex = i;
      bestClosest = candidate;
    }
  }
  if (bestIndex < 0) return null;
  return { index: bestIndex, closest: bestClosest, distance: bestDist };
}

/** Unit vector perpendicular to a wall edge, pointing INTO the polygon
 *  interior. Probes a small distance along each candidate perpendicular
 *  from the wall midpoint; whichever probe lands inside the polygon is
 *  the inward direction. Robust to either polygon winding (the rest of
 *  the codebase doesn't enforce a single winding convention).
 *
 *  Mirrors the same algorithm in `functions/src/prompt.ts` and
 *  `src/lib/doorClearance.ts`; consolidated here so the client can reuse
 *  it for "face into room" snapping without duplicating geometry code. */
export function inwardWallNormal(
  wallStart: Vec2,
  wallEnd: Vec2,
  polygon: Vec2[]
): Vec2 | null {
  const dir = wallDirection(wallStart, wallEnd);
  const mid: Vec2 = {
    x: (wallStart.x + wallEnd.x) / 2,
    y: (wallStart.y + wallEnd.y) / 2,
  };
  const candidates: Vec2[] = [
    { x: -dir.y, y: dir.x },
    { x: dir.y, y: -dir.x },
  ];
  // 1" probe is small enough to stay inside an L-shape pocket but large
  // enough to be unambiguous about which side of the wall is interior.
  const PROBE = 1;
  for (const cand of candidates) {
    const probe = {
      x: mid.x + cand.x * PROBE,
      y: mid.y + cand.y * PROBE,
    };
    if (pointInPolygon(probe, polygon)) return cand;
  }
  return null;
}

/** Compute a furniture rotation (degrees) such that the local +y "front"
 *  axis points in the direction of `targetNormal` (a unit vector). Returns
 *  the angle in `[0, 360)`.
 *
 *  Math: the local +y axis at rotation 0 is (0, 1). Our `furnitureCorners`
 *  applies a clockwise rotation by R: (x, y) -> (x cosR - y sinR, x sinR +
 *  y cosR). For (0, 1) this gives (-sinR, cosR). Setting this equal to
 *  (nx, ny) yields R = atan2(-nx, ny). */
export function rotationToFaceDirection(targetNormal: Vec2): number {
  const len = Math.hypot(targetNormal.x, targetNormal.y);
  if (len === 0) return 0;
  const nx = targetNormal.x / len;
  const ny = targetNormal.y / len;
  const rad = Math.atan2(-nx, ny);
  let deg = (rad * 180) / Math.PI;
  if (deg < 0) deg += 360;
  return deg;
}

export function snap(v: number, gridIn: number): number {
  return snapTo(v, gridIn, 0);
}

/** Snap a number to a grid of spacing `gridIn`, anchored so that `origin` is on the grid.
 *  Use this when the grid should be aligned to something other than world (0,0) — e.g.
 *  to a room's own coordinate origin so furniture aligns with the room's internal grid. */
export function snapTo(value: number, gridIn: number, origin: number): number {
  if (gridIn <= 0) return value;
  return Math.round((value - origin) / gridIn) * gridIn + origin;
}

/** Snap a 2D point to a grid (inches), anchored at world origin. */
export function snapVec(p: Vec2, gridIn: number): Vec2 {
  return { x: snap(p.x, gridIn), y: snap(p.y, gridIn) };
}

/** Snap a 2D point to a grid (inches), anchored at `origin`. */
export function snapVecTo(p: Vec2, gridIn: number, origin: Vec2): Vec2 {
  return {
    x: snapTo(p.x, gridIn, origin.x),
    y: snapTo(p.y, gridIn, origin.y),
  };
}

/** Compute the offset to apply to a furniture's center so that its axis-aligned
 *  bounding box (after rotation) lands on the nearest grid intersection, with the
 *  grid anchored at `origin`. Snapping the bounding-box corner — rather than the
 *  center — keeps the visible *edges* of the footprint flush with the grid lines.
 */
export function snapFurnitureToGrid(
  center: Vec2,
  width: number,
  depth: number,
  rotationDeg: number,
  gridIn: number,
  origin: Vec2
): Vec2 {
  if (gridIn <= 0) return center;
  const corners = furnitureCorners(
    center.x,
    center.y,
    width,
    depth,
    rotationDeg
  );
  const fbb = boundingBox(corners);
  const targetMinX = snapTo(fbb.minX, gridIn, origin.x);
  const targetMinY = snapTo(fbb.minY, gridIn, origin.y);
  return {
    x: center.x + (targetMinX - fbb.minX),
    y: center.y + (targetMinY - fbb.minY),
  };
}

/** Clamp a furniture footprint so that every corner of its (possibly rotated)
 *  rectangle stays inside the room polygon. This is what prevents a piece from
 *  being dragged through a wall.
 *
 *  Algorithm: iteratively find the corner that pokes through a wall by the
 *  greatest distance, project it back onto the nearest wall, and translate the
 *  whole footprint by that offset. Convex rooms converge in one pass;
 *  rectilinear non-convex rooms (L/U/T shapes) need at most a handful.
 *
 *  As a happy side effect, dragging a piece *toward* a wall pushes its
 *  bounding-box edge flush with the wall — i.e. you get "snap to wall" without
 *  needing a separate code path. */
export function clampFurnitureInside(
  center: Vec2,
  width: number,
  depth: number,
  rotationDeg: number,
  polygon: Vec2[]
): Vec2 {
  if (polygon.length < 3) return center;
  let cx = center.x;
  let cy = center.y;
  const MAX_ITERATIONS = 8;
  for (let i = 0; i < MAX_ITERATIONS; i++) {
    const corners = furnitureCorners(cx, cy, width, depth, rotationDeg);
    let worstOffset: Vec2 | null = null;
    let worstDist = 0;
    for (const c of corners) {
      if (pointInPolygon(c, polygon)) continue;
      const onPoly = clampInside(c, polygon);
      const dx = onPoly.x - c.x;
      const dy = onPoly.y - c.y;
      const d = Math.hypot(dx, dy);
      if (d > worstDist) {
        worstDist = d;
        worstOffset = { x: dx, y: dy };
      }
    }
    // Done as soon as every corner is inside (or on) the polygon. The 1e-3
    // floor guards against numerical jitter when a corner is exactly on a wall.
    if (!worstOffset || worstDist < 1e-3) break;
    cx += worstOffset.x;
    cy += worstOffset.y;
  }
  return { x: cx, y: cy };
}

/**
 * Polygon-footprint variant of {@link snapFurnitureToGrid}. `localOutline` is
 * the piece's footprint in footprint-local inches (origin at center, +y
 * front); the world-space bounding box of the rotated outline is snapped to
 * the grid exactly like the rectangle version.
 */
export function snapPolygonToGrid(
  center: Vec2,
  localOutline: Vec2[],
  rotationDeg: number,
  gridIn: number,
  origin: Vec2
): Vec2 {
  if (gridIn <= 0 || localOutline.length < 3) return center;
  const world = localToWorld(localOutline, center.x, center.y, rotationDeg);
  const fbb = boundingBox(world);
  const targetMinX = snapTo(fbb.minX, gridIn, origin.x);
  const targetMinY = snapTo(fbb.minY, gridIn, origin.y);
  return {
    x: center.x + (targetMinX - fbb.minX),
    y: center.y + (targetMinY - fbb.minY),
  };
}

/** Catch distance (inches) for aligning a dragged piece to another. */
export const FURNITURE_SNAP_INCHES = 6;

export interface FurnitureSnapTarget {
  center: Vec2;
  localOutline: Vec2[];
  rotationDeg: number;
}

/**
 * Keep `target` on the dominant axis through `origin`. Horizontal (or tied)
 * motion freezes Y so the piece slides along X; vertical motion freezes X.
 * Used for Shift+drag on the design canvas.
 */
export function lockToDominantAxis(origin: Vec2, target: Vec2): Vec2 {
  const dx = Math.abs(target.x - origin.x);
  const dy = Math.abs(target.y - origin.y);
  if (dx >= dy) return { x: target.x, y: origin.y };
  return { x: origin.x, y: target.y };
}

/**
 * Snap the world-space AABB of a footprint to the AABBs of `others`:
 * matching edges (aligned or flush) and centers, independently on X and Y.
 * `lock.x` / `lock.y` skip that axis so Shift axis-lock is not undone.
 */
export function snapPolygonToFurniture(
  center: Vec2,
  localOutline: Vec2[],
  rotationDeg: number,
  others: FurnitureSnapTarget[],
  tolIn: number = FURNITURE_SNAP_INCHES,
  lock?: { x?: boolean; y?: boolean }
): Vec2 {
  if (tolIn <= 0 || localOutline.length < 3 || others.length === 0) return center;
  const drag = boundingBox(localToWorld(localOutline, center.x, center.y, rotationDeg));
  const dragCx = (drag.minX + drag.maxX) / 2;
  const dragCy = (drag.minY + drag.maxY) / 2;

  let bestDx = tolIn;
  let bestDy = tolIn;
  let dx = 0;
  let dy = 0;

  for (const o of others) {
    if (o.localOutline.length < 3) continue;
    const other = boundingBox(
      localToWorld(o.localOutline, o.center.x, o.center.y, o.rotationDeg)
    );
    const otherCx = (other.minX + other.maxX) / 2;
    const otherCy = (other.minY + other.maxY) / 2;

    if (!lock?.x) {
      const xCandidates = [
        other.minX - drag.minX,
        other.maxX - drag.minX,
        other.minX - drag.maxX,
        other.maxX - drag.maxX,
        otherCx - dragCx,
      ];
      for (const c of xCandidates) {
        const ad = Math.abs(c);
        if (ad < bestDx) {
          bestDx = ad;
          dx = c;
        }
      }
    }
    if (!lock?.y) {
      const yCandidates = [
        other.minY - drag.minY,
        other.maxY - drag.minY,
        other.minY - drag.maxY,
        other.maxY - drag.maxY,
        otherCy - dragCy,
      ];
      for (const c of yCandidates) {
        const ad = Math.abs(c);
        if (ad < bestDy) {
          bestDy = ad;
          dy = c;
        }
      }
    }
  }

  return {
    x: lock?.x ? center.x : bestDx < tolIn ? center.x + dx : center.x,
    y: lock?.y ? center.y : bestDy < tolIn ? center.y + dy : center.y,
  };
}

/**
 * Polygon-footprint variant of {@link clampFurnitureInside}: iteratively push
 * the worst-protruding vertex of the rotated outline back onto the nearest
 * wall. Same convergence characteristics as the rectangle version.
 */
export function clampPolygonInside(
  center: Vec2,
  localOutline: Vec2[],
  rotationDeg: number,
  polygon: Vec2[]
): Vec2 {
  if (polygon.length < 3 || localOutline.length < 3) return center;
  let cx = center.x;
  let cy = center.y;
  const MAX_ITERATIONS = 10;
  for (let i = 0; i < MAX_ITERATIONS; i++) {
    const world = localToWorld(localOutline, cx, cy, rotationDeg);
    let worstOffset: Vec2 | null = null;
    let worstDist = 0;
    for (const c of world) {
      if (pointInPolygon(c, polygon)) continue;
      const onPoly = clampInside(c, polygon);
      const dx = onPoly.x - c.x;
      const dy = onPoly.y - c.y;
      const d = Math.hypot(dx, dy);
      if (d > worstDist) {
        worstDist = d;
        worstOffset = { x: dx, y: dy };
      }
    }
    if (!worstOffset || worstDist < 1e-3) break;
    cx += worstOffset.x;
    cy += worstOffset.y;
  }
  return { x: cx, y: cy };
}

/** Polygon-footprint variant of {@link furnitureFitsInRoom}. Strict on the
 *  boundary, and also rejects outlines whose edges cross a wall. */
export function polygonFitsInRoom(
  center: Vec2,
  localOutline: Vec2[],
  rotationDeg: number,
  polygon: Vec2[]
): boolean {
  if (polygon.length < 3 || localOutline.length < 3) return false;
  const world = localToWorld(localOutline, center.x, center.y, rotationDeg);
  return polygonInsidePolygon(world, polygon);
}

/** Rectangle of `thickness` centered on segment a→b, used as a solid wall. */
export function thickenedSegment(a: Vec2, b: Vec2, thickness: number): Vec2[] {
  const dir = wallDirection(a, b);
  const nx = -dir.y;
  const ny = dir.x;
  const h = thickness / 2;
  return [
    { x: a.x + nx * h, y: a.y + ny * h },
    { x: b.x + nx * h, y: b.y + ny * h },
    { x: b.x - nx * h, y: b.y - ny * h },
    { x: a.x - nx * h, y: a.y - ny * h },
  ];
}

/**
 * Push a footprint off interior wall segments. Each segment is treated as a
 * slab of `thickness` inches. The piece is translated along the wall normal
 * toward the side its center is already on.
 */
export function clampPolygonClearOfSegments(
  center: Vec2,
  localOutline: Vec2[],
  rotationDeg: number,
  segments: { a: Vec2; b: Vec2 }[],
  thickness: number
): Vec2 {
  if (segments.length === 0 || thickness <= 0 || localOutline.length < 3) return center;
  let cx = center.x;
  let cy = center.y;
  const half = thickness / 2;
  for (let iter = 0; iter < 8; iter++) {
    const world = localToWorld(localOutline, cx, cy, rotationDeg);
    let pushed = false;
    for (const seg of segments) {
      const rect = thickenedSegment(seg.a, seg.b, thickness);
      if (!polygonsOverlap(world, rect)) continue;
      const n = wallNormal(seg.a, seg.b);
      const mid = { x: (seg.a.x + seg.b.x) / 2, y: (seg.a.y + seg.b.y) / 2 };
      const side = Math.sign((cx - mid.x) * n.x + (cy - mid.y) * n.y) || 1;
      let pen = 0;
      for (const v of world) {
        const d = (v.x - seg.a.x) * n.x + (v.y - seg.a.y) * n.y;
        const into = half - side * d;
        if (into > pen) pen = into;
      }
      if (pen > 1e-3) {
        cx += n.x * side * (pen + 0.25);
        cy += n.y * side * (pen + 0.25);
        pushed = true;
        break;
      }
    }
    if (!pushed) break;
  }
  return { x: cx, y: cy };
}

/** Move a single vertex to a new absolute position. The polygon becomes "custom" if the
 *  edit visibly bends it away from the preset. (We don't enforce rectilinearity here —
 *  callers can pre-process newPos with axis-snap if they want straight walls.) */
export function moveVertex(points: Vec2[], index: number, newPos: Vec2): Vec2[] {
  return points.map((p, i) => (i === index ? newPos : p));
}

/** Slide an entire wall edge perpendicular to itself by `delta` inches.
 *  Both endpoints move together, which keeps the edge's length & direction the same and
 *  resizes the neighboring walls. */
export function slideEdge(
  points: Vec2[],
  edgeIndex: number,
  delta: number
): Vec2[] {
  const n = points.length;
  const a = points[edgeIndex];
  const b = points[(edgeIndex + 1) % n];
  const dir = wallDirection(a, b);
  // Outward normal (CCW input; this depends on orientation but for slide we accept either sign).
  const nx = -dir.y;
  const ny = dir.x;
  const dx = nx * delta;
  const dy = ny * delta;
  return points.map((p, i) => {
    if (i === edgeIndex || i === (edgeIndex + 1) % n) {
      return { x: p.x + dx, y: p.y + dy };
    }
    return p;
  });
}

/** Insert a new vertex on edge `edgeIndex` at parameter `t` ∈ [0,1] along the edge.
 *  Splits one wall into two. */
export function insertVertexOnEdge(
  points: Vec2[],
  edgeIndex: number,
  t = 0.5
): Vec2[] {
  const n = points.length;
  const a = points[edgeIndex];
  const b = points[(edgeIndex + 1) % n];
  const tt = Math.max(0.05, Math.min(0.95, t));
  const newPt: Vec2 = { x: a.x + (b.x - a.x) * tt, y: a.y + (b.y - a.y) * tt };
  const next = [...points];
  next.splice(edgeIndex + 1, 0, newPt);
  return next;
}

/** Remove vertex `index` (merging the two walls that met there into one).
 *  Returns the unchanged array if removing would drop below 3 vertices. */
export function removeVertex(points: Vec2[], index: number): Vec2[] {
  if (points.length <= 3) return points;
  return points.filter((_, i) => i !== index);
}

/** Snap a vertex position to be axis-aligned with neighboring vertices.
 *  If newPos.x is within `tolIn` of an existing vertex's x, lock to that x; same for y.
 *  Used while dragging to keep rooms naturally rectilinear. */
export function snapToAxis(
  newPos: Vec2,
  others: Vec2[],
  tolIn: number
): { pos: Vec2; snappedX: boolean; snappedY: boolean } {
  let x = newPos.x;
  let y = newPos.y;
  let snappedX = false;
  let snappedY = false;
  let bestDx = tolIn;
  let bestDy = tolIn;
  for (const o of others) {
    const dx = Math.abs(o.x - newPos.x);
    if (dx < bestDx) {
      bestDx = dx;
      x = o.x;
      snappedX = true;
    }
    const dy = Math.abs(o.y - newPos.y);
    if (dy < bestDy) {
      bestDy = dy;
      y = o.y;
      snappedY = true;
    }
  }
  return { pos: { x, y }, snappedX, snappedY };
}

/** Segments-intersect predicate (proper or improper intersection ignoring shared endpoints). */
function segmentsIntersect(p1: Vec2, p2: Vec2, p3: Vec2, p4: Vec2): boolean {
  function ccw(a: Vec2, b: Vec2, c: Vec2): number {
    return (c.y - a.y) * (b.x - a.x) - (b.y - a.y) * (c.x - a.x);
  }
  const d1 = ccw(p3, p4, p1);
  const d2 = ccw(p3, p4, p2);
  const d3 = ccw(p1, p2, p3);
  const d4 = ccw(p1, p2, p4);
  if (
    ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
    ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))
  ) {
    return true;
  }
  return false;
}

/** Convert a Vec2[] polygon to a polygon-clipping `Polygon` (with a single
 *  ring, repeating the first vertex at the end as the library expects). */
function toPCPolygon(points: Vec2[]): PCPolygon {
  if (points.length === 0) return [[]];
  const ring: PCRing = points.map((p) => [p.x, p.y] as [number, number]);
  // polygon-clipping requires the first and last vertex to coincide.
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first[0] !== last[0] || first[1] !== last[1]) {
    ring.push([first[0], first[1]]);
  }
  return [ring];
}

/** Convert a polygon-clipping Ring (closed) back to a Vec2[] (open). */
function fromPCRing(ring: PCRing): Vec2[] {
  const out: Vec2[] = [];
  for (let i = 0; i < ring.length; i++) {
    const [x, y] = ring[i];
    out.push({ x, y });
  }
  // Drop the closing duplicate if present.
  if (
    out.length >= 2 &&
    Math.abs(out[0].x - out[out.length - 1].x) < 1e-6 &&
    Math.abs(out[0].y - out[out.length - 1].y) < 1e-6
  ) {
    out.pop();
  }
  return out;
}

/** Union two simple polygons.
 *
 *  Returns the merged outer ring as a `Vec2[]`, or `null` if:
 *  - the union has more than one disjoint polygon (the addition doesn't
 *    touch the existing room — caller should snap to nearest wall and retry),
 *  - or the union has interior holes (rare for our convex-ish room shapes).
 *
 *  Wraps [`polygon-clipping`](https://github.com/mfogel/polygon-clipping). */
export function unionPolygons(a: Vec2[], b: Vec2[]): Vec2[] | null {
  if (a.length < 3 || b.length < 3) return null;
  try {
    const result = polygonClipping.union(toPCPolygon(a), toPCPolygon(b));
    if (result.length !== 1) return null;
    const poly = result[0];
    if (poly.length !== 1) return null; // has holes — bail
    const ring = poly[0];
    const points = fromPCRing(ring);
    if (points.length < 3) return null;
    return points;
  } catch {
    return null;
  }
}

/**
 * Union many simple polygons and return a single outer outline. Unlike
 * {@link unionPolygons} this is forgiving: if the union produces several
 * disjoint pieces or interior holes, it returns the outer ring of the
 * largest piece (holes dropped). Used for furniture footprints, where a
 * best-effort outline is far better than `null`.
 */
export function unionOutline(polys: Vec2[][]): Vec2[] | null {
  const valid = polys.filter((p) => p.length >= 3);
  if (valid.length === 0) return null;
  if (valid.length === 1) return valid[0].slice();
  try {
    const result = polygonClipping.union(...(valid.map(toPCPolygon) as [PCPolygon, ...PCPolygon[]]));
    let best: Vec2[] | null = null;
    let bestArea = -Infinity;
    for (const poly of result) {
      if (poly.length === 0) continue;
      const ring = fromPCRing(poly[0]);
      if (ring.length < 3) continue;
      const area = Math.abs(polygonArea(ring));
      if (area > bestArea) {
        bestArea = area;
        best = ring;
      }
    }
    return best;
  } catch {
    return null;
  }
}

/** Subtract `b` from `a` and return the outer ring of the largest remaining
 *  piece (holes dropped), or `null` when nothing remains. */
export function differenceOutline(a: Vec2[], b: Vec2[]): Vec2[] | null {
  if (a.length < 3) return null;
  if (b.length < 3) return a.slice();
  try {
    const result = polygonClipping.difference(toPCPolygon(a), toPCPolygon(b));
    let best: Vec2[] | null = null;
    let bestArea = -Infinity;
    for (const poly of result) {
      if (poly.length === 0) continue;
      const ring = fromPCRing(poly[0]);
      if (ring.length < 3) continue;
      const area = Math.abs(polygonArea(ring));
      if (area > bestArea) {
        bestArea = area;
        best = ring;
      }
    }
    return best;
  } catch {
    return null;
  }
}

/** Intersect two simple polygons and return the outer ring of the largest
 *  piece, or `null` when they don't overlap. */
export function intersectionOutline(a: Vec2[], b: Vec2[]): Vec2[] | null {
  if (a.length < 3 || b.length < 3) return null;
  try {
    const result = polygonClipping.intersection(toPCPolygon(a), toPCPolygon(b));
    let best: Vec2[] | null = null;
    let bestArea = -Infinity;
    for (const poly of result) {
      if (poly.length === 0) continue;
      const ring = fromPCRing(poly[0]);
      if (ring.length < 3) continue;
      const area = Math.abs(polygonArea(ring));
      if (area > bestArea) {
        bestArea = area;
        best = ring;
      }
    }
    return best;
  } catch {
    return null;
  }
}

/** Subtract every polygon in `clips` from `subject` and return the outer ring
 *  of each resulting disjoint piece (interior holes dropped). Returns the
 *  subject unchanged when there is nothing to clip, and falls back to the
 *  subject on a clipping error so a caller always gets at least one region. */
export function subtractPolygons(subject: Vec2[], clips: Vec2[][]): Vec2[][] {
  if (subject.length < 3) return [];
  const valid = clips.filter((c) => c.length >= 3);
  if (valid.length === 0) return [subject.slice()];
  try {
    const result = polygonClipping.difference(
      toPCPolygon(subject),
      ...(valid.map(toPCPolygon) as [PCPolygon, ...PCPolygon[]])
    );
    const out: Vec2[][] = [];
    for (const poly of result) {
      if (poly.length === 0) continue;
      const ring = fromPCRing(poly[0]);
      if (ring.length >= 3) out.push(ring);
    }
    return out.length > 0 ? out : [subject.slice()];
  } catch {
    return [subject.slice()];
  }
}

/** Area of the intersection of two simple polygons (0 when disjoint). */
export function polygonIntersectionArea(a: Vec2[], b: Vec2[]): number {
  if (a.length < 3 || b.length < 3) return 0;
  try {
    const result = polygonClipping.intersection(toPCPolygon(a), toPCPolygon(b));
    let total = 0;
    for (const poly of result) {
      if (poly.length === 0) continue;
      total += Math.abs(polygonArea(fromPCRing(poly[0])));
      for (let i = 1; i < poly.length; i++) {
        total -= Math.abs(polygonArea(fromPCRing(poly[i])));
      }
    }
    return total;
  } catch {
    return 0;
  }
}

/**
 * True iff two simple polygons overlap with positive area. Touching edges
 * and shared vertices count as *not* overlapping (matches the strict SAT
 * used for door clearance). Uses a cheap bbox reject, then a SAT fast path
 * for convex pairs, then an exact clipping-library intersection for
 * concave shapes (L / U sectionals).
 */
export function polygonsOverlap(a: Vec2[], b: Vec2[], eps = 1e-6): boolean {
  if (a.length < 3 || b.length < 3) return false;
  const ba = boundingBox(a);
  const bb = boundingBox(b);
  if (ba.maxX <= bb.minX + eps || bb.maxX <= ba.minX + eps) return false;
  if (ba.maxY <= bb.minY + eps || bb.maxY <= ba.minY + eps) return false;
  if (isConvex(a) && isConvex(b)) return convexPolygonsOverlapSAT(a, b, eps);
  return polygonIntersectionArea(a, b) > eps;
}

function isConvex(pts: Vec2[]): boolean {
  const n = pts.length;
  if (n < 4) return true;
  let sign = 0;
  for (let i = 0; i < n; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % n];
    const c = pts[(i + 2) % n];
    const cross = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
    if (Math.abs(cross) < 1e-9) continue;
    const s = cross > 0 ? 1 : -1;
    if (sign === 0) sign = s;
    else if (s !== sign) return false;
  }
  return true;
}

function convexPolygonsOverlapSAT(a: Vec2[], b: Vec2[], eps: number): boolean {
  const axes: Vec2[] = [];
  for (const poly of [a, b]) {
    for (let i = 0; i < poly.length; i++) {
      const p = poly[i];
      const q = poly[(i + 1) % poly.length];
      axes.push({ x: -(q.y - p.y), y: q.x - p.x });
    }
  }
  for (const axis of axes) {
    let aMin = Infinity,
      aMax = -Infinity,
      bMin = Infinity,
      bMax = -Infinity;
    for (const p of a) {
      const v = p.x * axis.x + p.y * axis.y;
      if (v < aMin) aMin = v;
      if (v > aMax) aMax = v;
    }
    for (const p of b) {
      const v = p.x * axis.x + p.y * axis.y;
      if (v < bMin) bMin = v;
      if (v > bMax) bMax = v;
    }
    if (aMax <= bMin + eps || bMax <= aMin + eps) return false;
  }
  return true;
}

/** Transform a footprint-local outline (inches, origin at center, +y front)
 *  into room coordinates given the piece's center and clockwise rotation. */
export function localToWorld(outline: Vec2[], cx: number, cy: number, rotationDeg: number): Vec2[] {
  const rad = (rotationDeg * Math.PI) / 180;
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  return outline.map((p) => ({
    x: cx + p.x * c - p.y * s,
    y: cy + p.x * s + p.y * c,
  }));
}

/** True iff every vertex of `poly` is strictly inside `room` and no edge of
 *  `poly` crosses a room wall (so a concave room can't "pass through" a
 *  vertex test). */
export function polygonInsidePolygon(poly: Vec2[], room: Vec2[]): boolean {
  if (poly.length < 3 || room.length < 3) return false;
  for (const p of poly) {
    if (!pointInPolygon(p, room)) return false;
  }
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    for (let j = 0; j < room.length; j++) {
      const c = room[j];
      const d = room[(j + 1) % room.length];
      if (segmentsIntersect(a, b, c, d)) return false;
    }
  }
  return true;
}

/** True if the polygon has no self-intersecting edges (excluding adjacent ones meeting at a vertex). */
export function isSimplePolygon(points: Vec2[]): boolean {
  const n = points.length;
  if (n < 3) return false;
  for (let i = 0; i < n; i++) {
    const a = points[i];
    const b = points[(i + 1) % n];
    for (let j = i + 1; j < n; j++) {
      // Skip adjacent edges (they share an endpoint).
      if (j === i) continue;
      if ((j + 1) % n === i) continue;
      if (j === (i + 1) % n) continue;
      const c = points[j];
      const d = points[(j + 1) % n];
      if (segmentsIntersect(a, b, c, d)) return false;
    }
  }
  return true;
}
